/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';
import { MenuButtonDropdownIcon } from '../PRIVATE_ThemedIcons/MenuButtonDropdownIcon';
import { Menu } from '../UNSAFE_Menu';
import { Text } from '../UNSAFE_Text';

import { BaseButton, StylingOptions } from '../UNSAFE_BaseButton';

import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import {
  SplitMenuButtonStyles,
  SplitMenuButtonVariantOptions
} from './themes/SplitMenuButtonStyles.css';
import { SplitMenuButtonRedwoodTheme } from './themes/redwood/SplitMenuButtonTheme.css';

import { ComponentChildren } from 'preact';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { useCallback, useState, useRef, useImperativeHandle } from 'preact/hooks';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type IntrinsicProps = Pick<
  HTMLAttributesSignalExcluded<HTMLElement>,
  | 'autofocus'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
  | 'aria-describedby'
>;

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type Props = IntrinsicProps &
  WidthInterpolationProps &
  TestIdProps & {
    /**
     * children - One or more MenuItems
     */
    children?: ComponentChildren;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * The label rendered on the action side of the button.  Used
     * to identify which child MenuItem action is invoked.
     */
    label?: string;

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     
     */
    variant?: 'outlined' | 'solid' | 'callToAction';

    /**
     * The size indicates how large the button is rendered: "sm", "md" (default), "lg".
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: () => void;
  };

const menuButtonStyling = [
  'embedded',
  'min',
  'noBorderRadiusStart',
  'noBorderStart'
] as StylingOptions[];
const menuButtonActiveStyling = [...menuButtonStyling, 'active'] as StylingOptions[];

const actionbuttonStyling = [
  'embedded',
  'fill',
  'noBorderRadiusEnd',
  'noBorderEnd'
] as StylingOptions[];
const actionButtonActiveStyling = [...actionbuttonStyling, 'active'] as StylingOptions[];

const interpolations = [...Object.values(dimensionInterpolations)];

const styleInterpolations = mergeInterpolations<DimensionProps>(interpolations);

/**
 * A SplitMenuButton is a combined action and menu button.
 */
export const SplitMenuButton = forwardRef(
  (
    {
      children,
      label = '',
      variant = 'outlined',
      isDisabled = false,
      size = 'md',
      testId,
      onAction,
      'aria-describedby': ariaDescribedBy,
      onBlur: propsOnBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart,
      ...props
    }: Props,
    ref?: ForwardedRef<HTMLSpanElement>
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isActive, setIsActive] = useState<boolean>(false);
    const { class: cls, ...styles } = styleInterpolations(props);

    // SPACE or ENTER on root area invokes action, as does CLICK/TAP on LEFT SIDE ACTION AREA
    // DOWN ARROW invokes menu, as does CLICK/TAP on RIGHT SIDE MENU AREA
    const onKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === ' ' || event.key === 'Enter') {
          setIsActive(true);
        } else if (event.key == 'ArrowDown') {
          !isDisabled && setIsMenuOpen((menuOpen: boolean) => !menuOpen);
          // Prevent page scroll for down arrow, user definitely wanted to show menu in this case.
          // Don't prevent other keypress defaults, as user may want to hold SPACE down to scroll page.
          event.preventDefault();
        }
      },
      [isDisabled, setIsMenuOpen, setIsActive]
    );

    const onKeyUp = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === ' ' || event.key === 'Enter') {
          !isDisabled && onAction && onAction();
          setIsActive(false);
        }
      },
      [isDisabled, onAction, setIsActive]
    );

    const onBlur = useCallback(() => {
      setIsActive(false);
    }, [setIsActive]);

    const _ref = useRef<HTMLSpanElement>(null);
    useImperativeHandle(ref!, () => _ref.current!, [_ref]);

    const menuHandler = () => {
      setIsMenuOpen(!isMenuOpen);
    };

    const handleClose = (e: any) => {
      //Focus has to be set on the launcher when menu is dismissed or
      //item is "selected"
      if (e.reason === 'dismissed' || e.reason === 'itemAction') {
        _ref.current?.focus();
      }
      setIsMenuOpen(false);
    };

    const { classes, styles: themeStyles } = useComponentTheme<
      SplitMenuButtonVariantOptions,
      SplitMenuButtonStyles
    >(SplitMenuButtonRedwoodTheme, {
      styleVariant: variant
    });

    const dividerStyles = classNames([
      themeStyles.divider,
      isDisabled && themeStyles.disabledDivider
    ]);

    const globalEventProps = {
      onBlur: propsOnBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart
    };

    const customGlobalEventProps = {
      onKeyDown: isDisabled ? undefined : onKeyDown,
      onKeyUp: onKeyUp,
      onBlur: onBlur
    };

    const rootStyles = classNames([classes, cls]);
    // Using Span for top button allows us to avoid :hover state rendering by
    // taking advantage of :enabled not on spans
    const rootButton = () => {
      return (
        <BaseButton
          elementDetails={{ type: 'span' }}
          ref={
            _ref as ForwardedRef<HTMLButtonElement> &
              ForwardedRef<HTMLAnchorElement> &
              ForwardedRef<HTMLSpanElement>
          }
          isDisabled={isDisabled}
          styling={['container', 'min']}
          variant={variant}
          size={size}
          testId={testId}
          aria-label={label}
          aria-roledescription="split menu button, press down arrow to invoke menu"
          aria-describedby={ariaDescribedBy}
          aria-expanded={isMenuOpen}>
          <TabbableModeContext.Provider value={{ isTabbable: false }}>
            <BaseButton
              elementDetails={{ type: 'span', isFocusable: false }}
              variant={variant}
              styling={getActionButtonStyling(isActive)}
              size={size}
              isDisabled={isDisabled}
              aria-hidden={true}
              onAction={onAction}>
              <Text truncation={'ellipsis'} weight={'inherit'}>
                {label}
              </Text>
            </BaseButton>
            <div class={`${dividerStyles}`} />
            <BaseButton
              elementDetails={{ type: 'span', isFocusable: false }}
              variant={variant}
              styling={getMenuButtonStyling(isMenuOpen)}
              size={size}
              isDisabled={isDisabled}
              aria-hidden={true}
              onAction={menuHandler}>
              <ButtonLabelLayout
                display="icons"
                startIcon={<MenuButtonDropdownIcon />}
                size={size}
                styling="embedded"></ButtonLabelLayout>
            </BaseButton>
          </TabbableModeContext.Provider>
        </BaseButton>
      );
    };

    if (isDisabled) {
      return (
        <span role="toolbar" class={rootStyles} style={styles}>
          {rootButton()}
        </span>
      );
    } else {
      /*TODO: Remove this hardcoded offsetValue so we have dropdown offset only set on one single place.
        Could be done via menu props or having a dropdownMenu component. JET-60595*/

      /*TODO: Once menu expose a prop to set min width we would need to calculate offset width of button
       so we set the correct min width since dropdown menus are 200px but if triggered element width is
       bigger should be that size. JET-60745 */
      return (
        <span
          role="toolbar"
          class={rootStyles}
          {...mergeProps(globalEventProps, customGlobalEventProps)}
          style={styles}>
          {rootButton()}
          <Menu anchorRef={_ref} isOpen={isMenuOpen} onClose={handleClose}>
            {children}
          </Menu>
        </span>
      );
    }
  }
);

function getMenuButtonStyling(isActive: boolean) {
  return isActive ? menuButtonActiveStyling : menuButtonStyling;
}

function getActionButtonStyling(isActive: boolean) {
  return isActive ? actionButtonActiveStyling : actionbuttonStyling;
}

SplitMenuButton.displayName = 'SplitMenuButton';
